import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
	AutoPopulationAttributeValue,
	AutoPopulationMapping,
	emptyAutoPopulationMapping,
	fetchAutoPopulationMapping,
	initiateAutoPopulation,
} from 'src/utils/autoPopulationService';
import { AUTOPOPULATION_OPTIONS_FIELD_PREFIX } from 'src/constants/CommonConstants';
import { logger } from 'src/utils/logger';
import { SnowAttribute, TaskDefinition, WorkflowIntention} from '@amzn/snow-nxt-workflow-service-typescript-client';
import {
	isTaskSpecificAttribute,
	isWorkflowSpecificAttribute,
} from 'src/utils/attributeTypeUtils';

const AUTO_POPULATION_DEBOUNCE_MS = 3000;

interface AutoPopulationContextType {
	handleAutoPopulation: (attributeId: string) => void;
	enrichRequirements: (requirements: any[]) => any[];
	isAutoPopulating: boolean;
	autoPopulationError: string;
	setAutoPopulationError: (error: string) => void;
	autopopulatingAttributes: Set<string>;
	baselineValues: Record<string, unknown>;
	setWorkflowIntention: (intention: WorkflowIntention | undefined) => void;
	isAttributeVisible: (attributeId: string) => boolean;
}

const AutoPopulationContext = createContext<AutoPopulationContextType | null>(null);

interface AutoPopulationProviderProps {
	workflowType: string;
	setValue: (name: string, value: any) => void;
	getValues: () => any;
	children: React.ReactNode;
	taskDefinitions: TaskDefinition[];
}

export const AutoPopulationProvider: React.FC<AutoPopulationProviderProps> = ({
	workflowType,
	setValue,
	getValues,
	children,
	taskDefinitions,
}) => {
	const [isAutoPopulating, setIsAutoPopulating] = useState(false);
	const [autoPopulationError, setAutoPopulationError] = useState('');
	const [autopopulatingAttributes, setAutopopulatingAttributes] = useState(new Set<string>());
	const [attributeVisibility, setAttributeVisibility] = useState<Record<string, boolean>>({});

	const timeoutsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
	const abortControllersRef = useRef<{ [key: string]: AbortController }>({});
	const autoPopulationMappingRef = useRef<AutoPopulationMapping>(emptyAutoPopulationMapping());
	const attributeVisibilityRef = useRef<Record<string, boolean>>({});
	const baselineValuesRef = useRef<Record<string, unknown>>({});
	const workflowIntentionRef = useRef<WorkflowIntention | undefined>(undefined);
	const isAutoPopulatingRef = useRef(false);

	const requirements = React.useMemo(() => {
		const commonMap = new Map(
			taskDefinitions
				.flatMap((taskDef: TaskDefinition) => taskDef.taskInputs?.filter(
					(attr: SnowAttribute) => isWorkflowSpecificAttribute(attr) && !attr.value
				) || [])
				.map((attr: SnowAttribute) => [attr.attributeId, attr])
		);
		const commonAttrs = [...commonMap.values()];
		const taskAttrs = taskDefinitions.flatMap((taskDef: TaskDefinition) =>
			taskDef.taskInputs?.filter(isTaskSpecificAttribute) || []
		);

		return [...commonAttrs, ...taskAttrs];
	}, [taskDefinitions]);

	const setWorkflowIntention = (intention: WorkflowIntention | undefined) => {
		workflowIntentionRef.current = intention;
	};

	const attributeHasRule = (attributeId: string): boolean =>
		!!autoPopulationMappingRef.current.definition?.[attributeId]?.visibilityCondition;

	const wasAttributeVisible = (attributeId: string): boolean =>
		attributeId in attributeVisibilityRef.current
			? attributeVisibilityRef.current[attributeId]
			: !attributeHasRule(attributeId);

	useEffect(() => {
		const fetchMapping = async () => {
			try {
				autoPopulationMappingRef.current = await fetchAutoPopulationMapping(workflowType);
			} catch (error) {
				await logger.error(`[AutoPopulation] definition lookup failed for workflowId=${workflowType}`, error as Error);
			}
		};

		fetchMapping();

		return () => {
			Object.values(abortControllersRef.current).forEach(ac => ac.abort());
			Object.values(timeoutsRef.current).forEach(t => clearTimeout(t));
		};
	}, [workflowType]);

	const handleAutoPopulation = useCallback((attributeId: string) => {
		if (isAutoPopulatingRef.current) return;

		const attributeIdParts = attributeId.split('.');
		const cleanAttributeId = attributeIdParts[1] || attributeId;
		const prefix = attributeIdParts[0] || 'EntityData';
		
		const definition = autoPopulationMappingRef.current.definition?.[cleanAttributeId];
		
		if (!definition?.dependents) return;

		if (cleanAttributeId === 'EntityName' && workflowIntentionRef.current === WorkflowIntention.UPDATE) {
			baselineValuesRef.current = {};
		}

		if (abortControllersRef.current[cleanAttributeId]) {
			abortControllersRef.current[cleanAttributeId].abort();
		}

		abortControllersRef.current[cleanAttributeId] = new AbortController();

		if (timeoutsRef.current[cleanAttributeId]) {
			clearTimeout(timeoutsRef.current[cleanAttributeId]);
		}
		
		timeoutsRef.current[cleanAttributeId] = setTimeout(async () => {
			isAutoPopulatingRef.current = true;
			setIsAutoPopulating(true);
			setAutopopulatingAttributes(prev => new Set([
				...prev,
				...(definition.dependents || []).map((id: string) => `${prefix}.${id}`)])
			);
			
			try {
				const allFormValues = getValues();
				const formData = allFormValues[prefix] || {};

				const attributeValues: Record<string, AutoPopulationAttributeValue> = {};

				requirements.forEach(req => {
					if (req.attributeId) {
						const value = formData[req.attributeId] ?? req.value;

						if (value != null && value !== '') {
							attributeValues[req.attributeId] = Array.isArray(value)
								? { listValue: value }
								: typeof value === 'number'
									? { numberValue: value }
									: typeof value === 'boolean'
										? { booleanValue: value }
										: { stringValue: String(value) };
						}
					}
				});

				const { populatedAttributes, attributeVisibility: incomingVisibility } = await initiateAutoPopulation({
					workflowId: workflowType,
					attributeUpdated: cleanAttributeId,
					attributeValues,
				});

				setAutoPopulationError('');

				if (Object.keys(incomingVisibility).length > 0) {
					const newlyHiddenIds = Object.entries(incomingVisibility)
						.filter(([id, visible]) => !visible && wasAttributeVisible(id))
						.map(([id]) => id);

					attributeVisibilityRef.current = { ...attributeVisibilityRef.current, ...incomingVisibility };
					setAttributeVisibility(attributeVisibilityRef.current);

					newlyHiddenIds.forEach((id) => setValue(`${prefix}.${id}`, undefined));
				}

				const populatedKeys = new Set(Object.keys(populatedAttributes));

				for (const depId of definition.dependents || []) {
					const depDef = autoPopulationMappingRef.current.definition?.[depId];

					if (depDef?.responseMapping) {
						for (const key of Object.keys(depDef.responseMapping)) {
							if (!populatedKeys.has(key)) {
								setValue(`${prefix}.${key}`, undefined);
							}
						}
					}
				}

				for (const [key, attributeValue] of Object.entries(populatedAttributes)) {
					if (attributeValue.listValue !== undefined) {
						setValue(`${AUTOPOPULATION_OPTIONS_FIELD_PREFIX}.${prefix}.${key}`, attributeValue.listValue);
						setValue(`${prefix}.${key}`, undefined);

						if (workflowIntentionRef.current === WorkflowIntention.UPDATE && !(key in baselineValuesRef.current)) {
							baselineValuesRef.current[key] = undefined;
						}
						continue;
					}

					const value = attributeValue.stringValue ??
						attributeValue.numberValue ??
						attributeValue.booleanValue;

					setValue(`${prefix}.${key}`, value);
					setValue(`${AUTOPOPULATION_OPTIONS_FIELD_PREFIX}.${prefix}.${key}`, undefined);

					if (workflowIntentionRef.current === WorkflowIntention.UPDATE && !(key in baselineValuesRef.current)) {
						baselineValuesRef.current[key] = value;
					}
				}
			} catch (error) {
				await logger.error(`[AutoPopulation] execution failed for attributeUpdated=${cleanAttributeId}`, error as Error);
				setAutoPopulationError(`Failed to auto-populate fields: ${error}`);
			} finally {
				isAutoPopulatingRef.current = false;
				setIsAutoPopulating(false);
				setAutopopulatingAttributes(prev => {
					const newSet = new Set(prev);

					(definition.dependents || []).forEach((id: string) => newSet.delete(`${prefix}.${id}`));

					return newSet;
				});
				delete timeoutsRef.current[cleanAttributeId];
			}
		}, AUTO_POPULATION_DEBOUNCE_MS);
	}, [workflowType, getValues, setValue, requirements]);

	const isAttributeVisible = useCallback((attributeId: string): boolean => {
		const cleanAttributeId = attributeId.split('.').pop() || attributeId;

		if (cleanAttributeId in attributeVisibility) {
			return attributeVisibility[cleanAttributeId];
		}

		return !attributeHasRule(cleanAttributeId);
	}, [attributeVisibility]);

	const enrichRequirements = (requirements: any[]) => {
		const definitions = autoPopulationMappingRef.current.definition || {};

		return requirements.map(req => ({
			...req,
			isOverridable: definitions[req.attributeId]?.isOverridable ?? undefined,
		}));
	};

	return (
		<AutoPopulationContext.Provider value={{
			handleAutoPopulation,
			enrichRequirements,
			isAutoPopulating,
			autoPopulationError,
			setAutoPopulationError,
			autopopulatingAttributes,
			baselineValues: baselineValuesRef.current,
			setWorkflowIntention,
			isAttributeVisible,
		}}>
			{children}
		</AutoPopulationContext.Provider>
	);
};

export const useAutoPopulation = () => {
	const context = useContext(AutoPopulationContext);

	if (!context) {
		throw new Error('useAutoPopulation must be used within AutoPopulationProvider');
	}

	return context;
};
